import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { safeStorage } from 'common/utils/safeStorage';

// Asking for a store rating at a good moment: right after Doqit has just
// done its job (a second reminder ticked off, or a search that found the
// document). The prompt itself lives in RatingPrompt.tsx; this file holds
// the rules and the triggers so they can be unit tested.

export const RATING_PROMPT_EVENT = 'doqit:rating-prompt';

export type RatingTrigger = 'reminder_completed' | 'search_result_opened';

export interface RatingEventDetail {
  trigger: RatingTrigger;
}

export const RATING_COOLDOWN_DAYS = 120;
const DAY_MS = 24 * 60 * 60 * 1000;

export const APP_STORE_ID = '6692611653';
export const ANDROID_PACKAGE = 'com.dr.doqit';

export const ratingKeys = (userId: string | number) => ({
  lastPrompted: `doqit_rating_last_prompted_${userId}`,
  remindersCompleted: `doqit_rating_reminders_completed_${userId}`,
});

// Paths where someone is still being onboarded or is mid sign-up/purchase.
const ONBOARDING_PATHS = ['/onboarding', '/first-document', '/tutorial', '/verify', '/signup', '/login',
  '/pricing', '/offer', '/success', '/mfa', '/welcome'];

export const isOnboardingPath = (pathname: string): boolean => (
  pathname === '/' || ONBOARDING_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`))
);

export interface RatingGateInput {
  now: Date;
  lastPromptedAt: string | null;
  onboarding: boolean;
  // True when RevenueCat knows the Pro entitlement but it is no longer
  // active: the app-side equivalent of rc_subscription_status = expired.
  expired: boolean;
}

export const shouldPromptRating = ({
  now, lastPromptedAt, onboarding, expired,
}: RatingGateInput): boolean => {
  if (onboarding || expired) return false;
  if (!lastPromptedAt) return true;
  const last = new Date(lastPromptedAt).getTime();
  if (Number.isNaN(last)) return true;
  return now.getTime() - last >= RATING_COOLDOWN_DAYS * DAY_MS;
};

// The second completed reminder is the moment; later ones fall back on the
// 120-day rest, so every completion from the second on is a candidate.
export const isRatingReminderCount = (count: number): boolean => count >= 2;

export const storeReviewUrl = (platform: string): string | null => {
  if (platform === 'ios') return `itms-apps://itunes.apple.com/app/id${APP_STORE_ID}?action=write-review`;
  if (platform === 'android') return `market://details?id=${ANDROID_PACKAGE}`;
  return null;
};

export const readRatingValue = async (key: string): Promise<string | null> => {
  try {
    if (Capacitor.isNativePlatform()) {
      const { value } = await Preferences.get({ key });
      if (value !== null) return value;
    }
  } catch {
    // fall through to localStorage
  }
  return safeStorage.getItem(key);
};

export const writeRatingValue = async (key: string, value: string): Promise<void> => {
  safeStorage.setItem(key, value);
  try {
    if (Capacitor.isNativePlatform()) await Preferences.set({ key, value });
  } catch {
    // best-effort
  }
};

export const requestRatingPrompt = (trigger: RatingTrigger): void => {
  window.dispatchEvent(new CustomEvent<RatingEventDetail>(RATING_PROMPT_EVENT, { detail: { trigger } }));
};

/** Call after a reminder is marked done. Asks from the second one on. */
export const noteReminderCompletedForRating = async (userId: string | number | null | undefined): Promise<void> => {
  if (userId == null) return;
  try {
    const key = ratingKeys(userId).remindersCompleted;
    const count = (Number(await readRatingValue(key)) || 0) + 1;
    await writeRatingValue(key, String(count));
    if (isRatingReminderCount(count)) requestRatingPrompt('reminder_completed');
  } catch {
    // never let a rating ask get in the way of the reminder itself
  }
};

/** Call when a search result is opened; the ask waits for the document to show. */
export const noteSearchResultOpenedForRating = (): void => {
  window.setTimeout(() => requestRatingPrompt('search_result_opened'), 1500);
};
